// Tiny shim so error boundaries / app code can report errors without
// statically importing @sentry/react (which would pull it back onto the
// critical path). Errors fired before Sentry finishes its deferred init
// are queued in main.tsx via the global 'error' / 'unhandledrejection'
// listeners — this helper re-uses the same dynamic import.
let cachedSentry: typeof import("@sentry/react") | null = null;
let loading: Promise<typeof import("@sentry/react")> | null = null;

async function getSentry() {
  if (cachedSentry) return cachedSentry;
  if (!loading) loading = import("@sentry/react");
  cachedSentry = await loading;
  return cachedSentry;
}

export function captureException(error: unknown): void {
  // Re-dispatch through window.onerror so the queue in main.tsx picks it
  // up if Sentry isn't ready yet; once ready this resolves quickly.
  void (async () => {
    try {
      const Sentry = await getSentry();
      Sentry.captureException(error);
    } catch (err) {
      console.warn('[Sentry] captureException failed:', err);
    }
  })();
}

export interface SentryUser {
  id: string;
  email?: string;
}

// Sentry's init is deferred (requestIdleCallback / 3s fallback in main.tsx),
// so an auth transition can easily land before the SDK exists. We keep the
// LATEST desired user here and apply it as soon as the module resolves —
// dynamic import() returns the same module promise, so a call made pre-init
// still lands (Sentry.setUser is a scope write and is safe to call before
// Sentry.init; init does not wipe the scope).
let pendingUser: SentryUser | null = null;
let applying = false;

export function setSentryUser(user: SentryUser | null): void {
  pendingUser = user;
  if (applying) return; // an in-flight apply will pick up the latest value
  applying = true;
  void (async () => {
    try {
      const Sentry = await getSentry();
      // Always apply the most recent value, not the one captured at call time,
      // so a rapid login → logout can't leave a stale user attached.
      Sentry.setUser(pendingUser ? { id: pendingUser.id, email: pendingUser.email } : null);
    } catch {
      // Telemetry must never break auth.
    } finally {
      applying = false;
    }
  })();
}
